import React, { useState, useEffect } from "react";
import axios from "axios";
import { AddIcon, Trash } from "../../assets/icons/icons";
import CustomAlert from "../CustomAlert";
import Pagination from "../section/Pagination";

export default function Settings() {
  const API_BASE = "https://laravel-api.aj-mart.com/api";

  // ========= axios instance مع توكن =========
  const getToken = () =>
    sessionStorage.getItem("authToken") ||
    localStorage.getItem("authToken") ||
    "";

  const API = axios.create({
    baseURL: API_BASE,
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    // إذا كنت تستخدم JWT، اترك withCredentials = false
    withCredentials: false,
  });

  // يضيف التوكن لكل طلب وقت الإرسال (دائمًا أحدث نسخة)
  API.interceptors.request.use((config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  // ========= الحالة العامة =========
  const [alertMsg, setAlertMsg] = useState({ type: "", message: "" });

  // Landing
  const [landingFile, setLandingFile] = useState(null);
  const [landingPreview, setLandingPreview] = useState("");
  const [landingBanners, setLandingBanners] = useState([]);

  // Offer
  const [offerFile, setOfferFile] = useState(null);
  const [offerPreview, setOfferPreview] = useState("");
  const [offerBanners, setOfferBanners] = useState([]);

  // Extra 1
  const [extraImage1File, setExtraImage1File] = useState(null);
  const [extraImage1Preview, setExtraImage1Preview] = useState("");
  const [extraImage1Banners, setExtraImage1Banners] = useState([]);

  // Extra 2
  const [extraImage2File, setExtraImage2File] = useState(null);
  const [extraImage2Preview, setExtraImage2Preview] = useState("");
  const [extraImage2Banners, setExtraImage2Banners] = useState([]);

  // حوار الحذف
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "landing", "offer", "extra1", "extra2"

  // ========= أدوات مساعدة =========
  const readPreview = (file, setter) => {
    const reader = new FileReader();
    reader.onloadend = () => setter(reader.result);
    reader.readAsDataURL(file);
  };

  const storageUrl = (path) =>
    `https://laravel-api.aj-mart.com/storage/app/public/${path}`;

  // ========= جلب كل البانرز =========
  const fetchBanners = async () => {
    try {
      const { data } = await API.get("/banners");
      if (data?.banners) {
        const all = data.banners;
        setLandingBanners(all.filter((b) => b.banner_landing_image));
        setOfferBanners(all.filter((b) => b.banner_offer_image));
        setExtraImage1Banners(all.filter((b) => b.banner_extra_image1));
        setExtraImage2Banners(all.filter((b) => b.banner_extra_image2));
      }
    } catch (error) {
      setAlertMsg({
        type: "error",
        message:
          error.response?.data?.message ||
          "Error fetching banners",
      });
      console.error("Error fetching banners:", error);
    }
  };

  useEffect(() => {
    // تحذير مبكر لو ما في توكن
    if (!getToken()) {
      setAlertMsg({
        type: "error",
        message: "Authentication required. Please log in again.",
      });
    }
    fetchBanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========= رفع صورة (دالة عامة) =========
  const uploadBanner = async (fieldName, file) => {
    if (!file) {
      setAlertMsg({ type: "error", message: "Please select an image first." });
      return;
    }
    // لو ما في توكن، أعطِ رسالة واضحة
    if (!getToken()) {
      setAlertMsg({
        type: "error",
        message: "Unauthorized (401). Please log in again.",
      });
      return;
    }
    const formData = new FormData();
    formData.append(fieldName, file);

    try {
      // لا تعيّن Content-Type يدوياً مع FormData
      const { data } = await API.post("/banners", formData);
      setAlertMsg({
        type: "success",
        message: data?.message || "Image uploaded successfully",
      });
      await fetchBanners();
    } catch (error) {
      const status = error.response?.status;
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Error uploading image";

      setAlertMsg({
        type: "error",
        message:
          status === 401
            ? "Unauthorized (401). Your session may have expired. Please log in again."
            : msg,
      });
      console.error("Upload error:", error);
    }
  };

  // ========= Handlers: Landing =========
  const handleLandingFileChange = (e) => {
    const file = e.target.files?.[0];
    setLandingFile(file || null);
    if (file) readPreview(file, setLandingPreview);
  };
  const handleLandingUpload = (e) => {
    e.preventDefault();
    uploadBanner("banner_landing_image", landingFile).then(() => {
      setLandingFile(null);
      setLandingPreview("");
    });
  };

  // ========= Handlers: Offer =========
  const handleOfferFileChange = (e) => {
    const file = e.target.files?.[0];
    setOfferFile(file || null);
    if (file) readPreview(file, setOfferPreview);
  };
  const handleOfferUpload = (e) => {
    e.preventDefault();
    uploadBanner("banner_offer_image", offerFile).then(() => {
      setOfferFile(null);
      setOfferPreview("");
    });
  };

  // ========= Handlers: Extra 1 =========
  const handleExtraImage1FileChange = (e) => {
    const file = e.target.files?.[0];
    setExtraImage1File(file || null);
    if (file) readPreview(file, setExtraImage1Preview);
  };
  const handleExtraImage1Upload = (e) => {
    e.preventDefault();
    uploadBanner("banner_extra_image1", extraImage1File).then(() => {
      setExtraImage1File(null);
      setExtraImage1Preview("");
    });
  };

  // ========= Handlers: Extra 2 =========
  const handleExtraImage2FileChange = (e) => {
    const file = e.target.files?.[0];
    setExtraImage2File(file || null);
    if (file) readPreview(file, setExtraImage2Preview);
  };
  const handleExtraImage2Upload = (e) => {
    e.preventDefault();
    uploadBanner("banner_extra_image2", extraImage2File).then(() => {
      setExtraImage2File(null);
      setExtraImage2Preview("");
    });
  };

  // ========= حذف بانر =========
  const openDeleteDialog = (banner, type) => {
    setBannerToDelete(banner);
    setDeleteType(type);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!bannerToDelete) return;
    if (!getToken()) {
      setAlertMsg({
        type: "error",
        message: "Unauthorized (401). Please log in again.",
      });
      return;
    }
    try {
      const { data } = await API.delete(`/banners/${bannerToDelete.id}`);
      setAlertMsg({
        type: "success",
        message: data?.message || "Banner deleted",
      });
      await fetchBanners();
    } catch (error) {
      const status = error.response?.status;
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Error deleting banner";
      setAlertMsg({
        type: "error",
        message:
          status === 401
            ? "Unauthorized (401). Your session may have expired. Please log in again."
            : msg,
      });
      console.error("Error deleting banner:", error);
    } finally {
      setShowDeleteDialog(false);
      setBannerToDelete(null);
      setDeleteType("");
    }
  };

  // ========= UI =========
  return (
    <>
      <section className="py-[25px] flex flex-col gap-[50px]">
        <div className="main-title">Settings</div>

        {alertMsg.message && (
          <CustomAlert
            type={alertMsg.type}
            message={alertMsg.message}
            onClose={() => setAlertMsg({ type: "", message: "" })}
          />
        )}

        {/* Landing */}
        <div>
          <h2 className="text-xl font-bold mb-4">Banner Landing Image</h2>
          <form onSubmit={handleLandingUpload} className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <label
                htmlFor="upload-landing"
                className="cursor-pointer border-2 border-[#1485a6] rounded bg-[#eeeeee6e] px-4 py-2"
              >
                {landingPreview ? (
                  <img
                    src={landingPreview}
                    alt="Preview"
                    className="w-16 h-16 object-cover"
                  />
                ) : (
                  "Upload Image"
                )}
              </label>
              <input
                type="file"
                id="upload-landing"
                accept="image/*"
                className="hidden"
                onChange={handleLandingFileChange}
              />
              <button type="submit" className="btn flex items-center gap-2">
                <AddIcon />
                <span>Save</span>
              </button>
            </div>
          </form>

          <div className="overflow-x-auto mt-4">
            <table className="w-full border-collapse border border-gray-200 text-left">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-200 p-2">IMG</th>
                  <th className="border border-gray-200 p-2">Control Tools</th>
                </tr>
              </thead>
              <tbody>
                {landingBanners.map((banner) => (
                  <tr key={banner.id} className="bg-white hover:bg-gray-50">
                    <td className="border border-gray-200 p-2">
                      <img
                        src={storageUrl(banner.banner_landing_image)}
                        alt=""
                        className="w-16 h-16 object-cover"
                      />
                    </td>
                    <td className="border border-gray-200 p-2">
                      <div className="flex items-center gap-3">
                        <div
                          className="cursor-pointer"
                          onClick={() => openDeleteDialog(banner, "landing")}
                        >
                          <Trash />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination />
          </div>
        </div>

        {/* Offer */}
        <div>
          <h2 className="text-xl font-bold mb-4">Banner Offer Image</h2>
          <form onSubmit={handleOfferUpload} className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <label
                htmlFor="upload-offer"
                className="cursor-pointer border-2 border-[#1485a6] rounded bg-[#eeeeee6e] px-4 py-2"
              >
                {offerPreview ? (
                  <img
                    src={offerPreview}
                    alt="Preview"
                    className="w-16 h-16 object-cover"
                  />
                ) : (
                  "Upload Image"
                )}
              </label>
              <input
                type="file"
                id="upload-offer"
                accept="image/*"
                className="hidden"
                onChange={handleOfferFileChange}
              />
              <button type="submit" className="btn flex items-center gap-2">
                <AddIcon />
                <span>Save</span>
              </button>
            </div>
          </form>

          <div className="overflow-x-auto mt-4">
            <table className="w-full border-collapse border border-gray-200 text-left">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-200 p-2">IMG</th>
                  <th className="border border-gray-200 p-2">Control Tools</th>
                </tr>
              </thead>
              <tbody>
                {offerBanners.map((banner) => (
                  <tr key={banner.id} className="bg-white hover:bg-gray-50">
                    <td className="border border-gray-200 p-2">
                      <img
                        src={storageUrl(banner.banner_offer_image)}
                        alt=""
                        className="w-16 h-16 object-cover"
                      />
                    </td>
                    <td className="border border-gray-200 p-2">
                      <div className="flex items-center gap-3">
                        <div
                          className="cursor-pointer"
                          onClick={() => openDeleteDialog(banner, "offer")}
                        >
                          <Trash />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination />
          </div>
        </div>

        {/* Extra 1 */}
        <div>
          <h2 className="text-xl font-bold mb-4">Banner Extra Image 1</h2>
          <form onSubmit={handleExtraImage1Upload} className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <label
                htmlFor="upload-extra1"
                className="cursor-pointer border-2 border-[#1485a6] rounded bg-[#eeeeee6e] px-4 py-2"
              >
                {extraImage1Preview ? (
                  <img
                    src={extraImage1Preview}
                    alt="Preview"
                    className="w-16 h-16 object-cover"
                  />
                ) : (
                  "Upload Image"
                )}
              </label>
              <input
                type="file"
                id="upload-extra1"
                accept="image/*"
                className="hidden"
                onChange={handleExtraImage1FileChange}
              />
              <button type="submit" className="btn flex items-center gap-2">
                <AddIcon />
                <span>Save</span>
              </button>
            </div>
          </form>

            <div className="overflow-x-auto mt-4">
              <table className="w-full border-collapse border border-gray-200 text-left">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-200 p-2">IMG</th>
                    <th className="border border-gray-200 p-2">Control Tools</th>
                  </tr>
                </thead>
                <tbody>
                  {extraImage1Banners.map((banner) => (
                    <tr key={banner.id} className="bg-white hover:bg-gray-50">
                      <td className="border border-gray-200 p-2">
                        <img
                          src={storageUrl(banner.banner_extra_image1)}
                          alt=""
                          className="w-16 h-16 object-cover"
                        />
                      </td>
                      <td className="border border-gray-200 p-2">
                        <div className="flex items-center gap-3">
                          <div
                            className="cursor-pointer"
                            onClick={() => openDeleteDialog(banner, "extra1")}
                          >
                            <Trash />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination />
            </div>
        </div>

        {/* Extra 2 */}
        <div>
          <h2 className="text-xl font-bold mb-4">Banner Extra Image 2</h2>
          <form onSubmit={handleExtraImage2Upload} className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <label
                htmlFor="upload-extra2"
                className="cursor-pointer border-2 border-[#1485a6] rounded bg-[#eeeeee6e] px-4 py-2"
              >
                {extraImage2Preview ? (
                  <img
                    src={extraImage2Preview}
                    alt="Preview"
                    className="w-16 h-16 object-cover"
                  />
                ) : (
                  "Upload Image"
                )}
              </label>
              <input
                type="file"
                id="upload-extra2"
                accept="image/*"
                className="hidden"
                onChange={handleExtraImage2FileChange}
              />
              <button type="submit" className="btn flex items-center gap-2">
                <AddIcon />
                <span>Save</span>
              </button>
            </div>
          </form>

          <div className="overflow-x-auto mt-4">
            <table className="w-full border-collapse border border-gray-200 text-left">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-200 p-2">IMG</th>
                  <th className="border border-gray-200 p-2">Control Tools</th>
                </tr>
              </thead>
              <tbody>
                {extraImage2Banners.map((banner) => (
                  <tr key={banner.id} className="bg-white hover:bg-gray-50">
                    <td className="border border-gray-200 p-2">
                      <img
                        src={storageUrl(banner.banner_extra_image2)}
                        alt=""
                        className="w-16 h-16 object-cover"
                      />
                    </td>
                    <td className="border border-gray-200 p-2">
                      <div className="flex items-center gap-3">
                        <div
                          className="cursor-pointer"
                          onClick={() => openDeleteDialog(banner, "extra2")}
                        >
                          <Trash />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination />
          </div>
        </div>
      </section>

      {/* حوار تأكيد الحذف */}
      {showDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-[400px] rounded-lg shadow-lg p-5 text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this banner?</p>
            <div className="flex justify-center gap-4">
              <button
                className="btn bg-gray-300 hover:bg-gray-400"
                onClick={() => setShowDeleteDialog(false)}
              >
                Cancel
              </button>
              <button className="btn bg-red-500 hover:bg-red-600" onClick={confirmDelete}>
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
